// Producer management — profile, licenses, and carrier credentialing (Calyvra admins / users.manage).
import { useState, useEffect, useCallback } from 'react';
import portalApi from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { UserCog, Loader2, Plus, Trash2, IdCard, Pencil, Upload, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const AGENT_STATUS = ['Active', 'Inactive'];
const RELATIONSHIP = ['Employee', '1099', 'Other'];
const LIC_STATUS = ['Active', 'Inactive', 'Expired', 'Pending'];
const CRED_STATUS = ['Pending', 'Appointed', 'Ready to Sell', 'Inactive', 'Terminated'];

export function ProducersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [constants, setConstants] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({});
  const [lic, setLic] = useState({ state: '', license_number: '', license_type: '', effective_date: '', expiration_date: '', status: 'Active' });
  const [car, setCar] = useState({ carrier: '', state: '', product_line: '', status: 'Pending', effective_date: '', termination_date: '', notes: '' });
  const [editLic, setEditLic] = useState(null);
  const [editCar, setEditCar] = useState(null);
  const [uploadingLic, setUploadingLic] = useState(null);

  useEffect(() => {
    portalApi.getAgents().then(setAgents).catch(() => {});
    portalApi.getConstants().then(setConstants).catch(() => {});
  }, []);

  const loadProducer = useCallback(async (id) => {
    setLoading(true);
    try {
      const d = await portalApi.getProducer(id);
      setData(d);
      setProfile(d.profile || {});
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selected) loadProducer(selected); }, [selected, loadProducer]);

  const saveProfile = async () => {
    try {
      await portalApi.updateProducer(selected, {
        phone: profile.phone, npn: profile.npn, resident_state: profile.resident_state,
        agent_status: profile.agent_status, relationship: profile.relationship, producer_notes: profile.producer_notes,
      });
      toast.success('Producer profile saved');
      loadProducer(selected);
    } catch (e) { toast.error(e.message); }
  };
  const addLicense = async () => { try { await portalApi.addProducerLicense(selected, lic); toast.success('License added'); setLic({ state: '', license_number: '', license_type: '', effective_date: '', expiration_date: '', status: 'Active' }); loadProducer(selected); } catch (e) { toast.error(e.message); } };
  const delLicense = async (id) => { await portalApi.deleteProducerLicense(selected, id); loadProducer(selected); };
  const addCarrier = async () => { try { await portalApi.addProducerCarrier(selected, car); toast.success('Carrier added'); setCar({ carrier: '', state: '', product_line: '', status: 'Pending', effective_date: '', termination_date: '', notes: '' }); loadProducer(selected); } catch (e) { toast.error(e.message); } };
  const delCarrier = async (id) => { await portalApi.deleteProducerCarrier(selected, id); loadProducer(selected); };

  const saveLicenseEdit = async () => {
    try {
      const { id, user_id, created_at, files, ...body } = editLic;
      await portalApi.updateProducerLicense(selected, id, body);
      toast.success('License updated'); setEditLic(null); loadProducer(selected);
    } catch (e) { toast.error(e.message); }
  };
  const saveCarrierEdit = async () => {
    try {
      const { id, user_id, created_at, ...body } = editCar;
      await portalApi.updateProducerCarrier(selected, id, body);
      toast.success('Carrier updated'); setEditCar(null); loadProducer(selected);
    } catch (e) { toast.error(e.message); }
  };
  const uploadLicense = async (licenseId, file) => {
    if (!file) return;
    setUploadingLic(licenseId);
    try { await portalApi.uploadLicenseFile(selected, licenseId, file); toast.success('License copy uploaded'); loadProducer(selected); }
    catch (e) { toast.error(e.message); } finally { setUploadingLic(null); }
  };
  const delLicenseFile = async (licenseId, fileId) => {
    try { await portalApi.deleteLicenseFile(selected, licenseId, fileId); loadProducer(selected); }
    catch (e) { toast.error(e.message); }
  };

  const P = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6" data-testid="producers-page">
      <div className="flex items-center gap-2"><UserCog className="h-6 w-6 text-sky-500" /><h1 className="text-2xl font-bold text-slate-900">Producers</h1></div>

      <div className="max-w-sm">
        <Label className="text-slate-600">Select Producer</Label>
        <Select value={selected || ''} onValueChange={setSelected}>
          <SelectTrigger data-testid="select-producer"><SelectValue placeholder="Choose a producer" /></SelectTrigger>
          <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading && <Loader2 className="h-6 w-6 animate-spin text-sky-500" />}

      {data && !loading && (
        <>
          <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="producer-profile">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><IdCard className="h-5 w-5 text-sky-500" /> {data.profile.name} — Producer Profile</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input value={data.profile.email || ''} disabled /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={profile.phone || ''} onChange={(e) => P('phone', e.target.value)} data-testid="producer-phone" /></div>
              <div className="space-y-2"><Label>NPN</Label><Input value={profile.npn || ''} onChange={(e) => P('npn', e.target.value)} data-testid="producer-npn" /></div>
              <div className="space-y-2"><Label>Resident State</Label><Select value={profile.resident_state || ''} onValueChange={(v) => P('resident_state', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{(constants?.us_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Agent Status</Label><Select value={profile.agent_status || ''} onValueChange={(v) => P('agent_status', v)}><SelectTrigger data-testid="producer-status"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{AGENT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Relationship</Label><Select value={profile.relationship || ''} onValueChange={(v) => P('relationship', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{RELATIONSHIP.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2 md:col-span-3"><Label>Notes</Label><Input value={profile.producer_notes || ''} onChange={(e) => P('producer_notes', e.target.value)} /></div>
            </div>
            <div className="flex justify-end mt-4"><Button onClick={saveProfile} data-testid="save-producer-btn">Save Profile</Button></div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="producer-licenses">
            <h3 className="font-semibold text-slate-900 mb-4">Licenses</h3>
            <div className="space-y-2 mb-4">
              {(data.licenses || []).map((l) => (
                <div key={l.id} className="p-3 border border-slate-100 rounded-lg text-sm" data-testid={`license-${l.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span><strong>{l.state}</strong> · {l.license_number || '—'} · {l.license_type || '—'} · <Badge className="bg-slate-100 text-slate-600">{l.status}</Badge>{l.expiration_date ? ` · exp ${l.expiration_date}` : ''}</span>
                    <div className="flex items-center gap-1">
                      <label className="cursor-pointer inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100" title="Upload license copy">
                        <input type="file" className="hidden" onChange={(e) => uploadLicense(l.id, e.target.files?.[0])} data-testid={`lic-upload-${l.id}`} />
                        {uploadingLic === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-slate-500" />}
                      </label>
                      <button onClick={() => setEditLic(l)} className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100" title="Edit license" data-testid={`lic-edit-${l.id}`}><Pencil className="h-4 w-4 text-slate-500" /></button>
                      {isAdmin && <button onClick={() => delLicense(l.id)} className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100" title="Delete license"><Trash2 className="h-4 w-4 text-red-600" /></button>}
                    </div>
                  </div>
                  {l.files?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {l.files.map((f) => (
                        <span key={f.id} className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 px-2 py-1 rounded" data-testid={`lic-file-${f.id}`}>
                          <a href={portalApi.getLicenseFileUrl(selected, l.id, f.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1"><Download className="h-3 w-3" /> {f.filename}</a>
                          {isAdmin && <button onClick={() => delLicenseFile(l.id, f.id)} className="text-red-500 ml-1" title="Remove copy"><Trash2 className="h-3 w-3" /></button>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {(data.licenses || []).length === 0 && <p className="text-slate-400 text-sm">No licenses yet.</p>}
            </div>
            <div className="grid md:grid-cols-6 gap-2 items-end">
              <Select value={lic.state || ''} onValueChange={(v) => setLic({ ...lic, state: v })}><SelectTrigger data-testid="lic-state"><SelectValue placeholder="State" /></SelectTrigger><SelectContent>{(constants?.us_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              <Input placeholder="License #" value={lic.license_number} onChange={(e) => setLic({ ...lic, license_number: e.target.value })} />
              <Input placeholder="Type" value={lic.license_type} onChange={(e) => setLic({ ...lic, license_type: e.target.value })} />
              <Input type="date" value={lic.expiration_date} onChange={(e) => setLic({ ...lic, expiration_date: e.target.value })} />
              <Select value={lic.status} onValueChange={(v) => setLic({ ...lic, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{LIC_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              <Button onClick={addLicense} disabled={!lic.state} data-testid="add-license-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="producer-carriers">
            <h3 className="font-semibold text-slate-900 mb-4">Carrier Credentialing / Appointments</h3>
            <div className="space-y-2 mb-4">
              {(data.carriers || []).map((cc) => (
                <div key={cc.id} className="flex items-center justify-between p-2 border border-slate-100 rounded-lg text-sm" data-testid={`carrier-${cc.id}`}>
                  <span><strong>{cc.carrier}</strong> · {cc.state || 'All states'} · {cc.product_line || '—'} · <Badge className="bg-sky-100 text-sky-700">{cc.status}</Badge>{cc.effective_date ? ` · eff ${cc.effective_date}` : ''}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditCar(cc)} className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100" title="Edit carrier" data-testid={`carrier-edit-${cc.id}`}><Pencil className="h-4 w-4 text-slate-500" /></button>
                    {isAdmin && <button onClick={() => delCarrier(cc.id)} className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100" title="Delete carrier"><Trash2 className="h-4 w-4 text-red-600" /></button>}
                  </div>
                </div>
              ))}
              {(data.carriers || []).length === 0 && <p className="text-slate-400 text-sm">No carrier relationships yet.</p>}
            </div>
            <div className="grid md:grid-cols-6 gap-2 items-end">
              <Input placeholder="Carrier" value={car.carrier} onChange={(e) => setCar({ ...car, carrier: e.target.value })} data-testid="car-carrier" />
              <Select value={car.state || ''} onValueChange={(v) => setCar({ ...car, state: v })}><SelectTrigger><SelectValue placeholder="State" /></SelectTrigger><SelectContent>{(constants?.us_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              <Input placeholder="Product Line" value={car.product_line} onChange={(e) => setCar({ ...car, product_line: e.target.value })} />
              <Select value={car.status} onValueChange={(v) => setCar({ ...car, status: v })}><SelectTrigger data-testid="car-status"><SelectValue /></SelectTrigger><SelectContent>{CRED_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              <Input type="date" value={car.effective_date} onChange={(e) => setCar({ ...car, effective_date: e.target.value })} />
              <Button onClick={addCarrier} disabled={!car.carrier} data-testid="add-carrier-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={Boolean(editLic)} onOpenChange={(o) => !o && setEditLic(null)}>
        <DialogContent data-testid="edit-license-dialog">
          <DialogHeader><DialogTitle>Edit License</DialogTitle></DialogHeader>
          {editLic && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>State</Label>
                <Select value={editLic.state || ''} onValueChange={(v) => setEditLic({ ...editLic, state: v })}><SelectTrigger><SelectValue placeholder="State" /></SelectTrigger><SelectContent>{(constants?.us_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1"><Label>License #</Label><Input value={editLic.license_number || ''} onChange={(e) => setEditLic({ ...editLic, license_number: e.target.value })} /></div>
              <div className="space-y-1"><Label>Type</Label><Input value={editLic.license_type || ''} onChange={(e) => setEditLic({ ...editLic, license_type: e.target.value })} /></div>
              <div className="space-y-1"><Label>Status</Label>
                <Select value={editLic.status || 'Active'} onValueChange={(v) => setEditLic({ ...editLic, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{LIC_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1"><Label>Effective Date</Label><Input type="date" value={editLic.effective_date || ''} onChange={(e) => setEditLic({ ...editLic, effective_date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Expiration Date</Label><Input type="date" value={editLic.expiration_date || ''} onChange={(e) => setEditLic({ ...editLic, expiration_date: e.target.value })} data-testid="edit-lic-expiration" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLic(null)}>Cancel</Button>
            <Button onClick={saveLicenseEdit} data-testid="save-license-edit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editCar)} onOpenChange={(o) => !o && setEditCar(null)}>
        <DialogContent data-testid="edit-carrier-dialog">
          <DialogHeader><DialogTitle>Edit Carrier</DialogTitle></DialogHeader>
          {editCar && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Carrier</Label><Input value={editCar.carrier || ''} onChange={(e) => setEditCar({ ...editCar, carrier: e.target.value })} /></div>
              <div className="space-y-1"><Label>State</Label>
                <Select value={editCar.state || ''} onValueChange={(v) => setEditCar({ ...editCar, state: v })}><SelectTrigger><SelectValue placeholder="State" /></SelectTrigger><SelectContent>{(constants?.us_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1"><Label>Product Line</Label><Input value={editCar.product_line || ''} onChange={(e) => setEditCar({ ...editCar, product_line: e.target.value })} /></div>
              <div className="space-y-1"><Label>Status</Label>
                <Select value={editCar.status || 'Pending'} onValueChange={(v) => setEditCar({ ...editCar, status: v })}><SelectTrigger data-testid="edit-car-status"><SelectValue /></SelectTrigger><SelectContent>{CRED_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1"><Label>Effective Date</Label><Input type="date" value={editCar.effective_date || ''} onChange={(e) => setEditCar({ ...editCar, effective_date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Termination Date</Label><Input type="date" value={editCar.termination_date || ''} onChange={(e) => setEditCar({ ...editCar, termination_date: e.target.value })} /></div>
              <div className="space-y-1 col-span-2"><Label>Notes</Label><Input value={editCar.notes || ''} onChange={(e) => setEditCar({ ...editCar, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCar(null)}>Cancel</Button>
            <Button onClick={saveCarrierEdit} data-testid="save-carrier-edit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProducersPage;
